import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  submitDailyReport,
  getMyReportStatus,
  getDailyReports,
  adminCreateDailyReport,
  updateDailyReport,
  deleteDailyReport,
  getManagers,
} from '../lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Loader2,
  FileText,
  CheckCircle,
  Users,
  UserCircle,
  Edit,
  Trash2,
  Plus,
  Calendar,
  UsersRound,
  MessageSquare,
  CalendarRange,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { ru } from 'date-fns/locale';

export default function DailyReportsPage() {
  const { user, isAdmin, isTeamLeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Manager state
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isManager, setIsManager] = useState(false);
  
  // Form state
  const [leadsWritten, setLeadsWritten] = useState('');
  const [contracts, setContracts] = useState('');
  const [newDeposits, setNewDeposits] = useState('');
  const [comment, setComment] = useState('');
  
  // Admin state
  const [reports, setReports] = useState(null);
  const [period, setPeriod] = useState('today');
  const [managers, setManagers] = useState([]);
  
  // Custom date range
  const [customDateFrom, setCustomDateFrom] = useState(null);
  const [customDateTo, setCustomDateTo] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [editLeadsWritten, setEditLeadsWritten] = useState('');
  const [editContracts, setEditContracts] = useState('');
  const [editNewDeposits, setEditNewDeposits] = useState('');
  const [editComment, setEditComment] = useState('');
  
  // Add dialog (admin)
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addManagerId, setAddManagerId] = useState('');
  const [addDate, setAddDate] = useState(new Date().toISOString().split('T')[0]);
  const [addLeadsWritten, setAddLeadsWritten] = useState('');
  const [addContracts, setAddContracts] = useState('');
  const [addNewDeposits, setAddNewDeposits] = useState('');
  const [addComment, setAddComment] = useState('');
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingReport, setDeletingReport] = useState(null);

  const fetchManagerStatus = useCallback(async () => {
    try {
      const status = await getMyReportStatus();
      setHasSubmitted(status.has_submitted);
      setIsManager(status.is_manager || !!user?.manager_id);
    } catch (err) {
      console.error('Failed to fetch status:', err);
      // Fallback to checking user object
      setIsManager(!!user?.manager_id);
    }
  }, [user?.manager_id]);

  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { period };
      
      // For custom date range
      if (period === 'custom' && customDateFrom && customDateTo) {
        delete params.period;
        params.date_from = format(customDateFrom, 'yyyy-MM-dd');
        params.date_to = format(customDateTo, 'yyyy-MM-dd');
      }
      
      const [reportsData, managersData] = await Promise.all([
        getDailyReports(params),
        isAdmin ? getManagers().catch(() => []) : Promise.resolve([])
      ]);
      setReports(reportsData);
      setManagers(managersData);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      toast.error('Ошибка загрузки отчётов');
    } finally {
      setLoading(false);
    }
  }, [period, customDateFrom, customDateTo, isAdmin]);

  useEffect(() => {
    if (isAdmin || isTeamLeader) {
      fetchAdminData();
      // Team leader also needs to check their own report status
      if (isTeamLeader) {
        fetchManagerStatus();
      }
    } else {
      // Check if user is a manager (has manager_id)
      const userIsManager = !!user?.manager_id;
      setIsManager(userIsManager);
      
      if (userIsManager) {
        fetchManagerStatus();
      }
      setLoading(false);
    }
  }, [isAdmin, isTeamLeader, fetchAdminData, fetchManagerStatus, user?.manager_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!leadsWritten || !contracts || !newDeposits) {
      toast.error('Заполните все обязательные поля');
      return;
    }

    try {
      setSubmitting(true);
      await submitDailyReport({
        leads_written: parseInt(leadsWritten),
        contracts: parseInt(contracts),
        new_deposits: parseInt(newDeposits),
        comment: comment || null,
      });
      setHasSubmitted(true);
      toast.success('Отчёт отправлен!');
    } catch (err) {
      console.error('Failed to submit:', err);
      toast.error(err.response?.data?.detail || 'Ошибка отправки отчёта');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (report) => {
    setEditingReport(report);
    setEditLeadsWritten(report.leads_written.toString());
    setEditContracts(report.contracts.toString());
    setEditNewDeposits(report.new_deposits.toString());
    setEditComment(report.comment || '');
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingReport) return;

    try {
      setSubmitting(true);
      await updateDailyReport(editingReport.id, {
        leads_written: parseInt(editLeadsWritten),
        contracts: parseInt(editContracts),
        new_deposits: parseInt(editNewDeposits),
        comment: editComment || null,
      });
      toast.success('Отчёт обновлён');
      setEditDialogOpen(false);
      fetchAdminData();
    } catch (err) {
      console.error('Failed to update:', err);
      toast.error(err.response?.data?.detail || 'Ошибка обновления');
    } finally {
      setSubmitting(false);
    }
  };

  const openAddDialog = () => {
    setAddManagerId('');
    setAddDate(new Date().toISOString().split('T')[0]);
    setAddLeadsWritten('');
    setAddContracts('');
    setAddNewDeposits('');
    setAddComment('');
    setAddDialogOpen(true);
  };

  const handleAddSave = async () => {
    if (!addManagerId || !addDate || !addLeadsWritten || !addContracts || !addNewDeposits) {
      toast.error('Заполните все обязательные поля');
      return;
    }

    try {
      setSubmitting(true);
      await adminCreateDailyReport(addManagerId, addDate, {
        leads_written: parseInt(addLeadsWritten),
        contracts: parseInt(addContracts),
        new_deposits: parseInt(addNewDeposits),
        comment: addComment || null,
      });
      toast.success('Отчёт создан');
      setAddDialogOpen(false);
      fetchAdminData();
    } catch (err) {
      console.error('Failed to create:', err);
      toast.error(err.response?.data?.detail || 'Ошибка создания');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = (report) => {
    setDeletingReport(report);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingReport) return;

    try {
      await deleteDailyReport(deletingReport.id);
      toast.success('Отчёт удалён');
      setDeleteDialogOpen(false);
      setDeletingReport(null);
      fetchAdminData();
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  // Manager view (not admin, not team leader) - submit form or success message
  if (!isAdmin && !isTeamLeader) {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    if (!isManager) {
      return (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">Дневной отчёт</h1>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">У вас нет доступа к дневным отчётам</p>
              <p className="text-sm text-muted-foreground mt-2">
                (Нужна привязка к менеджеру)
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (hasSubmitted) {
      return (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold">Дневной отчёт</h1>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Ваш отчёт принят!</h2>
              <p className="text-muted-foreground text-center">
                Спасибо! Ваш дневной отчёт за сегодня уже отправлен.
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Дневной отчёт</h1>
        
        <Card>
          <CardHeader>
            <CardTitle>Отчёт за сегодня</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="leadsWritten">Сколько написала лидов? *</Label>
                <Input
                  id="leadsWritten"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={leadsWritten}
                  onChange={(e) => setLeadsWritten(e.target.value)}
                  required
                  data-testid="leads-written-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contracts">Сколько пошло на договор? *</Label>
                <Input
                  id="contracts"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={contracts}
                  onChange={(e) => setContracts(e.target.value)}
                  required
                  data-testid="contracts-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newDeposits">Сколько новых залито за сегодня? *</Label>
                <Input
                  id="newDeposits"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={newDeposits}
                  onChange={(e) => setNewDeposits(e.target.value)}
                  required
                  data-testid="new-deposits-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="comment">Комментарий (необязательно)</Label>
                <Textarea
                  id="comment"
                  placeholder="Дополнительная информация..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  data-testid="comment-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
                data-testid="submit-report-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                Отправить отчёт
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin / Team Leader dashboard view
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Дневные отчёты</h1>
          <p className="text-muted-foreground">
            {isTeamLeader ? 'Отчёты вашей команды' : 'Статистика по менеджерам'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openAddDialog} data-testid="add-report-btn">
            <Plus className="h-4 w-4 mr-2" />
            Добавить
          </Button>
        )}
      </div>

      {/* Team leader own report form */}
      {isTeamLeader && !hasSubmitted && (isManager || user?.manager_id) && (
        <Card className="border-primary/30">
          <CardHeader>
            <CardTitle className="text-lg">Ваш отчёт за сегодня</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="leadsWritten">Написала лидов *</Label>
                  <Input
                    id="leadsWritten"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={leadsWritten}
                    onChange={(e) => setLeadsWritten(e.target.value)}
                    required
                    data-testid="leads-written-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contracts">На договор *</Label>
                  <Input
                    id="contracts"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={contracts}
                    onChange={(e) => setContracts(e.target.value)}
                    required
                    data-testid="contracts-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newDeposits">Новых залито *</Label>
                  <Input
                    id="newDeposits"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={newDeposits}
                    onChange={(e) => setNewDeposits(e.target.value)}
                    required
                    data-testid="new-deposits-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="comment">Комментарий</Label>
                <Textarea
                  id="comment"
                  placeholder="Дополнительная информация..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  data-testid="comment-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
                data-testid="submit-report-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                Отправить отчёт
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {isTeamLeader && hasSubmitted && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardContent className="flex items-center gap-3 py-4">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <p className="text-sm">Ваш отчёт за сегодня отправлен</p>
          </CardContent>
        </Card>
      )}

      {/* Period filter */}
      <div className="flex flex-wrap items-center gap-2">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        <Select value={period} onValueChange={(val) => {
          setPeriod(val);
          if (val !== 'custom') {
            setCustomDateFrom(null);
            setCustomDateTo(null);
          }
        }}>
          <SelectTrigger className="w-40" data-testid="period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Сегодня</SelectItem>
            <SelectItem value="yesterday">Вчера</SelectItem>
            <SelectItem value="week">Неделя</SelectItem>
            <SelectItem value="month">Месяц</SelectItem>
            <SelectItem value="three_months">3 месяца</SelectItem>
            <SelectItem value="year">Год</SelectItem>
            <SelectItem value="custom">Произвольная дата</SelectItem>
          </SelectContent>
        </Select>
        
        {period === 'custom' && (
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="custom-date-btn">
                <CalendarRange className="h-4 w-4" />
                {customDateFrom && customDateTo ? (
                  <span className="text-sm">
                    {format(customDateFrom, 'd MMM', { locale: ru })} - {format(customDateTo, 'd MMM', { locale: ru })}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Выбрать даты</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4" align="start">
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground">С</Label>
                  <CalendarComponent
                    mode="single"
                    selected={customDateFrom}
                    onSelect={setCustomDateFrom}
                    locale={ru}
                    className="rounded-md border mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">По</Label>
                  <CalendarComponent
                    mode="single"
                    selected={customDateTo}
                    onSelect={setCustomDateTo}
                    locale={ru}
                    className="rounded-md border mt-1"
                  />
                </div>
                <Button 
                  className="w-full" 
                  onClick={() => setDatePickerOpen(false)}
                  disabled={!customDateFrom || !customDateTo}
                >
                  Применить
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Totals card */}
      {reports && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Общая статистика</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-primary">{reports.totals.leads_written}</p>
                <p className="text-sm text-muted-foreground">Написала</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{reports.totals.contracts}</p>
                <p className="text-sm text-muted-foreground">Договоров</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{reports.totals.new_deposits}</p>
                <p className="text-sm text-muted-foreground">Новых залито</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Teams accordion */}
      {reports && reports.by_team.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Нет отчётов за выбранный период</p>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="multiple" defaultValue={reports?.by_team.map(t => t.team_id || 'no-team')} className="space-y-4">
          {reports?.by_team.map((team) => (
            <AccordionItem
              key={team.team_id || 'no-team'}
              value={team.team_id || 'no-team'}
              className="border rounded-lg"
            >
              <AccordionTrigger className="px-4 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <UsersRound className="h-5 w-5 text-primary" />
                    </div>
                    <div className="text-left">
                      <span className="font-medium">{team.team_name}</span>
                      <div className="text-sm text-muted-foreground">
                        {team.reports.length} отчётов
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="font-semibold">{team.totals.leads_written}</p>
                      <p className="text-xs text-muted-foreground">написала</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-green-600">{team.totals.contracts}</p>
                      <p className="text-xs text-muted-foreground">договоров</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-blue-600">{team.totals.new_deposits}</p>
                      <p className="text-xs text-muted-foreground">залито</p>
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-3">
                  {team.reports.map((report) => (
                    <Card key={report.id} className="bg-muted/30">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-background flex items-center justify-center">
                              <UserCircle className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium">{report.manager_name}</p>
                              <p className="text-xs text-muted-foreground">{report.date}</p>
                            </div>
                          </div>
                          {isAdmin && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditDialog(report)}
                              data-testid={`edit-report-${report.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => confirmDelete(report)}
                              data-testid={`delete-report-${report.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 mt-4 text-center">
                          <div className="p-2 rounded-lg bg-background">
                            <p className="font-semibold">{report.leads_written}</p>
                            <p className="text-xs text-muted-foreground">написала</p>
                          </div>
                          <div className="p-2 rounded-lg bg-background">
                            <p className="font-semibold text-green-600">{report.contracts}</p>
                            <p className="text-xs text-muted-foreground">договоров</p>
                          </div>
                          <div className="p-2 rounded-lg bg-background">
                            <p className="font-semibold text-blue-600">{report.new_deposits}</p>
                            <p className="text-xs text-muted-foreground">залито</p>
                          </div>
                        </div>
                        
                        {report.comment && (
                          <div className="mt-3 p-2 rounded-lg bg-background flex items-start gap-2">
                            <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5" />
                            <p className="text-sm">{report.comment}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редактировать отчёт</DialogTitle>
            <DialogDescription>
              {editingReport?.manager_name} • {editingReport?.date}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Сколько написала лидов?</Label>
              <Input
                type="number"
                min="0"
                value={editLeadsWritten}
                onChange={(e) => setEditLeadsWritten(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Сколько пошло на договор?</Label>
              <Input
                type="number"
                min="0"
                value={editContracts}
                onChange={(e) => setEditContracts(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Сколько новых залито?</Label>
              <Input
                type="number"
                min="0"
                value={editNewDeposits}
                onChange={(e) => setEditNewDeposits(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Комментарий</Label>
              <Textarea
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleEditSave} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Добавить отчёт</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Менеджер *</Label>
              <Select value={addManagerId} onValueChange={setAddManagerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Выберите менеджера" />
                </SelectTrigger>
                <SelectContent>
                  {managers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Дата *</Label>
              <Input
                type="date"
                value={addDate}
                onChange={(e) => setAddDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Сколько написала лидов? *</Label>
              <Input
                type="number"
                min="0"
                value={addLeadsWritten}
                onChange={(e) => setAddLeadsWritten(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Сколько пошло на договор? *</Label>
              <Input
                type="number"
                min="0"
                value={addContracts}
                onChange={(e) => setAddContracts(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Сколько новых залито? *</Label>
              <Input
                type="number"
                min="0"
                value={addNewDeposits}
                onChange={(e) => setAddNewDeposits(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Комментарий</Label>
              <Textarea
                value={addComment}
                onChange={(e) => setAddComment(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleAddSave} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Создать'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить отчёт?</AlertDialogTitle>
            <AlertDialogDescription>
              Отчёт {deletingReport?.manager_name} за {deletingReport?.date} будет удалён.
              Менеджер сможет отправить новый отчёт за этот день.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
